/**
 * Coerce model output to a single display/copy string (handles string or common object shapes).
 * @returns {string|undefined}
 */
export function normalizeAddress(raw) {
  if (raw == null) return undefined;
  if (typeof raw === "string") {
    const t = raw.trim();
    return t || undefined;
  }
  if (Array.isArray(raw)) {
    const joined = raw
      .map((x) => (typeof x === "string" ? x.trim() : normalizeAddress(x)))
      .filter((x) => typeof x === "string" && x.length > 0)
      .join(", ");
    return joined || undefined;
  }
  if (typeof raw === "object") {
    const formatted =
      (typeof raw.formatted_address === "string" && raw.formatted_address.trim()) ||
      (typeof raw.formatted === "string" && raw.formatted.trim()) ||
      (typeof raw.full === "string" && raw.full.trim()) ||
      (typeof raw.display === "string" && raw.display.trim()) ||
      (typeof raw.vicinity === "string" && raw.vicinity.trim()) ||
      (typeof raw.location === "string" && raw.location.trim()) ||
      (typeof raw.text === "string" && raw.text.trim()) ||
      "";
    if (formatted) return formatted;
    if (raw.address != null && typeof raw.address === "object") {
      const inner = normalizeAddress(raw.address);
      if (inner) return inner;
    }
    const streetLine = [raw.street_number, raw.streetNumber, raw.route, raw.street]
      .filter((x) => x != null && String(x).trim() !== "")
      .map((x) => String(x).trim())
      .join(" ")
      .trim();
    const parts = [
      streetLine || undefined,
      raw.street1,
      raw.line1,
      raw.addressLine1,
      typeof raw.address === "string" ? raw.address : undefined,
      raw.city,
      raw.locality,
      raw.state,
      raw.administrative_area_level_1,
      raw.region,
      raw.zip,
      raw.postal_code,
    ]
      .filter((x) => x != null && String(x).trim() !== "")
      .map((x) => String(x).trim());
    if (parts.length) return parts.join(", ");
    if (raw.postalAddress && typeof raw.postalAddress === "object") {
      const nested = normalizeAddress(raw.postalAddress);
      if (nested) return nested;
    }
    return undefined;
  }
  const s = String(raw).trim();
  return s || undefined;
}

/**
 * @returns {Array<{ days: string, times: string }>|undefined}
 */
export function normalizeHoursEntries(raw) {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return undefined;
    const entries = [];
    for (const item of raw) {
      if (item == null) continue;
      if (typeof item === "string") {
        const t = item.trim();
        if (t) entries.push({ days: "", times: t });
      } else if (typeof item === "object") {
        const days = String(item.days ?? item.label ?? item.day ?? "").trim();
        const times = String(item.times ?? item.open ?? item.hours ?? item.text ?? "").trim();
        if (days || times) entries.push({ days, times });
      }
    }
    return entries.length ? entries : undefined;
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return undefined;
    return [{ days: "", times: t }];
  }
  return undefined;
}

/**
 * Strip ``` / ```json fences whether they wrap the whole reply or sit inside prose.
 * @param {string} s
 * @returns {string}
 */
function stripMarkdownJsonFences(s) {
  const t = s.trim();
  const whole = /^```(?:json)?\s*([\s\S]*?)```\s*$/im;
  const m0 = t.match(whole);
  if (m0) return m0[1].trim();
  const any = /```(?:json)?\s*([\s\S]*?)```/im;
  const m1 = t.match(any);
  if (m1) {
    const inner = m1[1].trim();
    if (inner.startsWith("[") || inner.startsWith("{")) return inner;
  }
  return t;
}

/**
 * @param {unknown} input
 * @returns {string}
 */
function coerceAiResponseToString(input) {
  if (input == null) return "";
  if (typeof input === "string") return input;
  if (typeof input === "object") {
    const o = /** @type {Record<string, unknown>} */ (input);
    const c = o.content ?? o.text ?? o.message ?? o.body;
    if (typeof c === "string") return c;
    if (typeof o.response === "string") return o.response;
  }
  return "";
}

/**
 * @param {unknown[]} results
 * @returns {Array<object>}
 */
function normalizeHousePickAiRestaurantRows(results) {
  return results.map((r) => {
    if (r == null || typeof r !== "object") {
      return {
        name: undefined,
        description: undefined,
        address: undefined,
        phone: undefined,
        website: undefined,
        hours: undefined,
        coordinates: undefined,
      };
    }
    const row = /** @type {Record<string, unknown>} */ (r);
    const coords = row.coordinates;
    let latitude;
    let longitude;
    if (coords && typeof coords === "object") {
      const c = /** @type {Record<string, unknown>} */ (coords);
      latitude = c.latitude ?? c.lat;
      longitude = c.longitude ?? c.lng ?? c.lon;
    }
    const latNum = latitude != null ? Number(latitude) : NaN;
    const lngNum = longitude != null ? Number(longitude) : NaN;

    const website =
      typeof row.website === "string"
        ? row.website.trim()
        : row.website != null
          ? String(row.website).trim()
          : "";
    const phone =
      typeof row.phone === "string"
        ? row.phone.trim()
        : row.phone != null
          ? String(row.phone).trim()
          : "";
    const hours = normalizeHoursEntries(row.hours);

    return {
      name: row.name,
      description: row.description,
      address: normalizeAddress(row.address),
      phone: phone || undefined,
      website: website || undefined,
      hours,
      coordinates:
        Number.isFinite(latNum) && Number.isFinite(lngNum)
          ? { latitude: latNum, longitude: lngNum }
          : coords,
    };
  });
}

/**
 * Parse JSON from model text (OpenAI or Vertex) into House Pick result rows.
 * Strips ```json fences (including when the model adds intro text). Accepts one object or an array.
 * Never throws — returns [] on failure so callers can still refetch.
 * @param {unknown} input
 */
export function parseHousePickAiResponseText(input) {
  if (Array.isArray(input)) {
    return normalizeHousePickAiRestaurantRows(input);
  }
  if (input != null && typeof input === "object") {
    const o = /** @type {Record<string, unknown>} */ (input);
    if (Array.isArray(o.results)) {
      return normalizeHousePickAiRestaurantRows(o.results);
    }
    const str = coerceAiResponseToString(input);
    if (str) return parseHousePickAiResponseText(str);
    return [];
  }

  const text = typeof input === "string" ? input : coerceAiResponseToString(input);
  if (!text || !String(text).trim()) {
    return [];
  }

  let s = stripMarkdownJsonFences(String(text));

  let parsed;
  try {
    parsed = JSON.parse(s);
  } catch {
    const start = s.indexOf("[");
    const end = s.lastIndexOf("]");
    if (start < 0 || end <= start) {
      return [];
    }
    try {
      parsed = JSON.parse(s.slice(start, end + 1));
    } catch {
      return [];
    }
  }

  const results = Array.isArray(parsed) ? parsed : [parsed];
  return normalizeHousePickAiRestaurantRows(results);
}
